import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FiUploadCloud } from "react-icons/fi";
import FileUploadPreviewList from "components/FileUploadPreviewList";
import {
  describeAcceptHelper,
  parseAcceptToAllowedMimeTypes,
  validateFileTypeWithSize,
} from "utils/validateFileTypeWithSize";
import { API_ENDPOINTS } from "lib/apiUrl";
import { uploadFile } from "lib/uploadFile";

function getItemIdentifier(item) {
  return item?.id || item?.url || item?.file?.name || "";
}

function getItemUrl(item) {
  if (!item) return "";
  if (typeof item.file === "string") return item.file;
  if (item.url) return item.url;
  return "";
}

function mapItemsSetUploading(prev, itemId) {
  return prev.map((item) =>
    getItemIdentifier(item) === itemId ? { ...item, status: "uploading", progress: 0 } : item,
  );
}

function mapItemsApplyProgressTick(prev, itemId) {
  return prev.map((item) => {
    if (getItemIdentifier(item) !== itemId) return item;
    if (item.status !== "uploading") return item;
    const current = item.progress ?? 0;
    return { ...item, progress: Math.min(current + 6, 92) };
  });
}

function mapItemsMarkUploadComplete(prev, itemId, url) {
  return prev.map((item) =>
    getItemIdentifier(item) === itemId
      ? { ...item, url, file: url, status: "complete", progress: 100 }
      : item,
  );
}

function mapItemsResetUploadIdle(prev, itemId) {
  return prev.map((item) =>
    getItemIdentifier(item) === itemId ? { ...item, status: "idle", progress: 0 } : item,
  );
}

export default function FileUpload({
  title = "Upload files",
  accept = "*",
  multiple = true,
  helperText,
  minSizeMB = 0,
  maxSizeMB = 5,
  dimensions = [],
  allowedMimeTypes: allowedMimeTypesProp,
  items: controlledItems,
  onItemsChange,
  onUpload,
  onOpen,
  onDelete,
  /** Relative API path for `uploadFile()` (default: image/media `API_ENDPOINTS.uploadFile`). */
  uploadPath,
  /** Extra `FormData` fields sent with the file (e.g. `{ kind: "document" }` if your API uses one route). */
  uploadFormFields,
  showPreviewActions = true,
  showPreviewProgress = true,
}) {
  const [items, setItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);
  const uploadIntervalsRef = useRef({});
  const isControlled = Array.isArray(controlledItems);
  const resolvedItems = isControlled ? controlledItems : items;

  const displayHelperText = useMemo(() => {
    if (helperText != null && String(helperText).length > 0) {
      return helperText;
    }
    return describeAcceptHelper(accept, minSizeMB, maxSizeMB);
  }, [helperText, accept, minSizeMB, maxSizeMB]);

  const updateItems = (updater) => {
    if (isControlled) {
      const nextValue = typeof updater === "function" ? updater(resolvedItems) : updater;
      onItemsChange?.(nextValue);
      return;
    }

    setItems((prev) => {
      const nextValue = typeof updater === "function" ? updater(prev) : updater;
      onItemsChange?.(nextValue);
      return nextValue;
    });
  };

  const clearNativeFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const clearUploadInterval = (id) => {
    if (uploadIntervalsRef.current[id]) {
      clearInterval(uploadIntervalsRef.current[id]);
      delete uploadIntervalsRef.current[id];
    }
  };

  useEffect(() => {
    return () => {
      Object.values(uploadIntervalsRef.current).forEach((timer) => clearInterval(timer));
      uploadIntervalsRef.current = {};
    };
  }, []);

  const processFileList = async (fileList) => {
    if (!fileList?.length) return;

    const files = multiple ? [...fileList] : [fileList[0]];
    const allowedList = allowedMimeTypesProp ?? parseAcceptToAllowedMimeTypes(accept);

    setValidationError("");

    const validMapped = [];
    const errors = [];

    for (const file of files) {
      if (!file) continue;
      const result = await validateFileTypeWithSize(
        file,
        allowedList,
        minSizeMB,
        maxSizeMB,
        dimensions,
      );
      if (result.isValid) {
        validMapped.push({
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
          file,
          progress: 0,
          status: "idle",
        });
      } else {
        errors.push(`${file.name}: ${result.message}`);
      }
    }

    if (errors.length > 0) {
      setValidationError(errors.join(" "));
    }

    if (validMapped.length > 0) {
      updateItems((prev) => (multiple ? [...prev, ...validMapped] : validMapped.slice(0, 1)));
    }

    clearNativeFileInput();
  };

  const onInputChange = async (event) => {
    const { files } = event.target;
    if (files?.length) {
      await processFileList(files);
    } else {
      clearNativeFileInput();
    }
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files?.length) {
      await processFileList(event.dataTransfer.files);
    }
  };

  const handleUpload = async (id, itemArg) => {
    clearUploadInterval(id);
    setValidationError("");
    updateItems((prev) => mapItemsSetUploading(prev, id));

    const activeItem = itemArg || resolvedItems.find((item) => getItemIdentifier(item) === id);
    if (onUpload) {
      onUpload(activeItem);
      return;
    }

    if (!activeItem?.file || typeof activeItem.file === "string") {
      return;
    }

    const bumpProgress = () => {
      updateItems((prev) => mapItemsApplyProgressTick(prev, id));
    };
    uploadIntervalsRef.current[id] = window.setInterval(bumpProgress, 180);

    try {
      const path = uploadPath ?? API_ENDPOINTS.uploadFile;
      const url = await uploadFile(activeItem.file, {
        path,
        extraFields: uploadFormFields,
      });
      clearUploadInterval(id);
      updateItems((prev) => mapItemsMarkUploadComplete(prev, id, url));
    } catch (err) {
      clearUploadInterval(id);
      const message = err?.message || "Upload failed. Please try again.";
      setValidationError(message);
      updateItems((prev) => mapItemsResetUploadIdle(prev, id));
    }
  };

  const handleOpen = (item) => {
    if (onOpen) {
      onOpen(item);
      return;
    }

    const awsOrRemoteUrl = getItemUrl(item);
    if (awsOrRemoteUrl) {
      window.open(awsOrRemoteUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (item?.file) {
      const localUrl = URL.createObjectURL(item.file);
      window.open(localUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(localUrl), 3000);
    }
  };

  const handleDelete = (id, itemArg) => {
    clearUploadInterval(id);
    const activeItem = itemArg || resolvedItems.find((item) => getItemIdentifier(item) === id);
    onDelete?.(activeItem);
    updateItems((prev) => prev.filter((item) => getItemIdentifier(item) !== id));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <div className="mb-3 text-sm font-semibold text-text-700">{title}</div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`w-full rounded-xl border-2 border-dashed p-7 text-center transition ${
          isDragging
            ? "border-brand-900 bg-brand-50"
            : "border-slate-300 bg-slate-50 hover:border-brand-500"
        } ${validationError ? "border-red-300 bg-red-50/40" : ""}`}
      >
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-900">
          <FiUploadCloud />
        </div>
        <div className="text-sm font-semibold text-text-800">
          Click to upload or drag and drop
        </div>
        <div className="mt-1 text-xs text-text-500">{displayHelperText}</div>
      </button>

      {validationError ? (
        <p className="mt-2 text-xs font-medium text-red-600" role="alert" aria-live="polite">
          {validationError}
        </p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={onInputChange}
      />

      <FileUploadPreviewList
        items={resolvedItems}
        showActions={showPreviewActions}
        showProgress={showPreviewProgress}
        onUpload={handleUpload}
        onOpen={handleOpen}
        onDelete={handleDelete}
      />
    </div>
  );
}

FileUpload.propTypes = {
  title: PropTypes.string,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  helperText: PropTypes.string,
  minSizeMB: PropTypes.number,
  maxSizeMB: PropTypes.number,
  dimensions: PropTypes.arrayOf(PropTypes.number),
  allowedMimeTypes: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      file: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
        }),
      ]),
      url: PropTypes.string,
      status: PropTypes.string,
      progress: PropTypes.number,
    }),
  ),
  onItemsChange: PropTypes.func,
  onUpload: PropTypes.func,
  onOpen: PropTypes.func,
  onDelete: PropTypes.func,
  uploadPath: PropTypes.string,
  uploadFormFields: PropTypes.objectOf(PropTypes.string),
  showPreviewActions: PropTypes.bool,
  showPreviewProgress: PropTypes.bool,
};
